import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { createSimplePubSub } from '../../util/simple-pub-sub';
import { PostOfficeRouteProvider } from '../store';
var noop = function noop() {};
var RouteListenerContext = /*#__PURE__*/createContext({
  update: noop
});
export var createRouteSubscriber = function createRouteSubscriber() {
  return createSimplePubSub();
};
export var useRouteListenerContext = function useRouteListenerContext() {
  return useContext(RouteListenerContext);
};
export var useRouteListener = function useRouteListener() {
  var ref = useRef();
  if (!ref.current) {
    ref.current = createRouteSubscriber();
  }
  var _ref$current = _slicedToArray(ref.current, 2),
    routeSubscriber = _ref$current[0],
    updateRoute = _ref$current[1];
  var routeConfig = useMemo(function () {
    return {
      initialUrl: new URL(window.location.href),
      routeSubscriber: routeSubscriber
    };
  }, [routeSubscriber]);
  var routeUpdater = useMemo(function () {
    return {
      update: updateRoute
    };
  }, [updateRoute]);
  return [routeConfig, routeUpdater];
};

/**
 * Custom hook to integrate post-office with your router. This allows placement to subscriber to route updates
 * This hook is specifically designed to work in a browser environment and will not execute in server-side or non-browser environments.
 *
 * Note: This requires PostOfficeProvider to be in the tree above the use of this hook.
 *
 * @param {string[]} deps - An array of dependencies that, when changed, will trigger the update of the route context.
 * This array should include any variables or state that, when updated, should cause the route context to be updated with the current URL.
 * In practise this is likely to be location information from your router.
 *
 * @example
 * // Example usage with React Resource Router
 * const ReactResourceRouterListener = ({ children }: { children: React.ReactNode }) => {
 *	const [route] = useRouter();
 *
 *	usePostOfficeRouteUpdate([route.basePath, route.location.pathname]);
 *
 *	return <>{children}</>;
 *};
 */
export var usePostOfficeRouteUpdate = function usePostOfficeRouteUpdate(deps) {
  var _useRouteListenerCont = useRouteListenerContext(),
    update = _useRouteListenerCont.update;
  useEffect(function () {
    if (typeof window === 'undefined') {
      return;
    }
    update({
      next: new URL(window.location.href)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
export var RouteListenerProvider = function RouteListenerProvider(_ref) {
  var children = _ref.children;
  var _useRouteListener = useRouteListener(),
    _useRouteListener2 = _slicedToArray(_useRouteListener, 2),
    route = _useRouteListener2[0],
    routeUpdater = _useRouteListener2[1];
  return /*#__PURE__*/React.createElement(RouteListenerContext.Provider, {
    value: routeUpdater
  }, /*#__PURE__*/React.createElement(PostOfficeRouteProvider, route, children));
};